import { ref } from 'vue'
import apiService from '../services/apiService'
import { formatDuration } from '../utils/dateHelpers'

export function useImageUpload(toast) {
  // Image upload
  const selectedImages = ref([])
  const uploading = ref(false)
  const imageUploadProgress = ref(0)

  // ZIP upload
  const selectedZipFile = ref(null)
  const zipForceOverwrite = ref(false)
  const uploadProgress = ref({
    total: 0,
    processed: 0,
    uploaded: 0,
    failed: 0,
    replaced: 0,
    skipped: 0,
    errors: []
  })
  const uploadStats = ref({
    startTime: null,
    speed: 0,
    eta: 0
  })
  const uploadStages = ref([])
  const currentUploadStage = ref('')
  const uploadStatus = ref('Готов к загрузке')
  const canCancelUpload = ref(false)
  const uploadFinished = ref(false)
  const uploadResult = ref(null)
  const activeUploadId = ref(null)
  const progressPollHandle = ref(null)

  const IMAGE_MIME_PREFIX = 'image/'
  const MAX_FILES_PER_UPLOAD = 1000
  const MAX_FILE_SIZE = 200 * 1024 * 1024

  const stopProgressPolling = () => {
    if (progressPollHandle.value) {
      clearInterval(progressPollHandle.value)
      progressPollHandle.value = null
    }
  }

  const applyProgressUpdate = (progress) => {
    uploadProgress.value.total = progress.totalFiles || uploadProgress.value.total || 0
    uploadProgress.value.processed = progress.processedFiles || 0
    uploadProgress.value.uploaded = progress.uploadedFiles || 0
    uploadProgress.value.failed = progress.failedFiles || 0
    uploadProgress.value.replaced = progress.replacedFiles || 0
    uploadProgress.value.skipped = progress.skippedFiles || 0
    uploadProgress.value.errors = progress.errors || []

    uploadStats.value.speed = progress.speed || 0
    uploadStats.value.eta = progress.etaSeconds || 0

    if (progress.statusMessage) {
      uploadStatus.value = progress.statusMessage
    }

    currentUploadStage.value = progress.currentStage || ''

    if (Array.isArray(progress.stages) && progress.stages.length > 0) {
      uploadStages.value = progress.stages
    }
  }

  const startProgressPolling = (uploadId) => {
    stopProgressPolling()
    if (!uploadId) return
    activeUploadId.value = uploadId

    progressPollHandle.value = setInterval(async () => {
      try {
        const progress = await apiService.getUploadProgress(uploadId)
        applyProgressUpdate(progress)
        if (progress.currentStage === 'complete' || progress.currentStage === 'error') {
          stopProgressPolling()
        }
      } catch (err) {
        if (err?.response?.status === 404) return
        console.error('Ошибка получения прогресса:', err)
      }
    }, 800)
  }

  const resetUploadState = () => {
    stopProgressPolling()
    uploadProgress.value = {
      total: 0, processed: 0, uploaded: 0,
      failed: 0, replaced: 0, skipped: 0, errors: []
    }
    uploadStats.value = { startTime: null, speed: 0, eta: 0 }
    uploadStages.value = []
    currentUploadStage.value = ''
    uploadStatus.value = 'Готов к загрузке'
    uploadFinished.value = false
    uploadResult.value = null
    activeUploadId.value = null
    // zipForceOverwrite не сбрасываем здесь: resetUploadState вызывается из uploadZip()
    // ПОСЛЕ того как пользователь поставил галку. Сброс делается явно в acknowledgeUploadResult,
    // cancelZipUpload и onZipSelect — когда пользователь действительно начинает заново.
  }

  const isShallowPath = (path) => {
    const segments = path.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean)
    return segments.length < 2
  }

  const onImageFilesChange = (event) => {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    if (files.length > MAX_FILES_PER_UPLOAD) {
      toast.add({ severity: 'warn', summary: 'Лимит файлов', detail: `Максимум ${MAX_FILES_PER_UPLOAD} файлов за раз`, life: 5000 })
      event.target.value = ''
      return
    }

    const validFiles = []
    const imageExts = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.tiff', '.svg', '.avif']
    for (const file of files) {
      if (!file.type.startsWith(IMAGE_MIME_PREFIX) && !imageExts.some(ext => file.name.toLowerCase().endsWith(ext))) {
        toast.add({ severity: 'warn', summary: 'Пропущен', detail: `${file.name}: неподдерживаемый формат`, life: 3000 })
        continue
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.add({ severity: 'warn', summary: 'Пропущен', detail: `${file.name}: размер превышает 200 МБ`, life: 3000 })
        continue
      }
      validFiles.push(file)
    }

    if (validFiles.length > 0) {
      selectedImages.value = [...selectedImages.value, ...validFiles]
    }
    event.target.value = ''
  }

  const clearSelectedImages = () => {
    selectedImages.value = []
  }

  const removeSelectedImage = (index) => {
    selectedImages.value.splice(index, 1)
  }

  const uploadImages = async (targetPath, refreshDataFn, closeDialogFn) => {
    if (selectedImages.value.length === 0) return

    uploading.value = true
    imageUploadProgress.value = 0
    try {
      if (isShallowPath(targetPath)) {
        toast.add({ severity: 'error', summary: 'Запрещено', detail: 'Загрузка файлов в эту папку запрещена. Выберите подпапку (например, папку бренда).', life: 5000 })
        uploading.value = false
        return
      }
      const useChunked = selectedImages.value.length > 10
      let result

      if (useChunked) {
        result = await apiService.uploadImagesToFolderChunked(
          targetPath,
          selectedImages.value,
          (progress) => { imageUploadProgress.value = Math.round(progress) }
        )
      } else {
        result = await apiService.uploadImagesToFolder(targetPath, selectedImages.value)
      }

      imageUploadProgress.value = 100

      if (result.replaced_duplicates > 0) {
        toast.add({
          severity: 'info',
          summary: 'Дубликаты заменены',
          detail: `${result.replaced_duplicates} дубликатов было заменено новыми файлами`
        })
      }

      toast.add({ severity: 'success', summary: 'Успех', detail: result.message })
      selectedImages.value = []
      if (closeDialogFn) closeDialogFn()
      if (refreshDataFn) refreshDataFn()
    } catch (error) {
      console.error('Ошибка загрузки изображений:', error)
      toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить изображения' })
    } finally {
      uploading.value = false
      imageUploadProgress.value = 0
    }
  }

  const onZipSelect = (event) => {
    resetUploadState()
    zipForceOverwrite.value = false
    selectedZipFile.value = event.files[0]
  }

  const uploadZip = async (targetPath, refreshDataFn) => {
    if (!selectedZipFile.value) return

    resetUploadState()
    uploading.value = true
    canCancelUpload.value = false
    uploadStats.value.startTime = Date.now()
    uploadStatus.value = 'Подготовка архива к отправке...'

    const clientUploadId = (globalThis.crypto?.randomUUID?.() || `upload-${Date.now()}`)
    activeUploadId.value = clientUploadId

    try {
      const sizeMb = selectedZipFile.value.size ? selectedZipFile.value.size / (1024 * 1024) : 0
      await apiService.initUploadProgress(clientUploadId, selectedZipFile.value.name, sizeMb)

      uploadStatus.value = 'Передача архива на сервер...'
      startProgressPolling(clientUploadId)

      const result = await apiService.uploadZipToFolder(targetPath, selectedZipFile.value, clientUploadId, zipForceOverwrite.value)

      stopProgressPolling()
      uploadFinished.value = true

      const totalFiles = result.total_processed || result.totalFiles || uploadProgress.value.total || (result.uploaded.length + result.failed.length)

      uploadProgress.value.total = totalFiles
      uploadProgress.value.processed = totalFiles
      uploadProgress.value.uploaded = result.uploaded.length
      uploadProgress.value.failed = result.failed.length
      uploadProgress.value.replaced = result.replaced_duplicates || 0
      uploadProgress.value.skipped = result.skipped || 0
      uploadProgress.value.errors = result.failed || []

      uploadStages.value = uploadStages.value.map(s => ({ ...s, active: false, completed: true }))
      currentUploadStage.value = 'complete'

      const elapsed = (Date.now() - uploadStats.value.startTime) / 1000
      uploadStats.value.speed = elapsed > 0 && totalFiles > 0 ? Math.round(totalFiles / elapsed) : 0
      uploadStats.value.eta = 0

      const successMsg = result.message || `Загружено ${result.uploaded.length} из ${totalFiles} файлов`
      uploadStatus.value = successMsg
      uploadResult.value = {
        status: result.failed.length === 0 ? 'success' : 'warn',
        message: successMsg,
        failed: result.failed,
        uploaded: result.uploaded.length,
        replaced: result.replaced_duplicates || 0
      }

      toast.add({
        severity: result.failed.length === 0 ? 'success' : 'warn',
        summary: result.failed.length === 0 ? 'Успех' : 'Частично загружено',
        detail: successMsg,
        life: 5000
      })

      if (refreshDataFn) refreshDataFn()
    } catch (error) {
      stopProgressPolling()
      console.error('Ошибка загрузки ZIP:', error)
      const errorMsg = error.response?.data?.detail || error.message || 'Не удалось загрузить ZIP архив'

      uploadFinished.value = true
      uploadStatus.value = errorMsg
      uploadProgress.value.errors = [{ filename: 'archive', error: errorMsg }]

      uploadStages.value = uploadStages.value.map(s => ({ ...s, active: false }))
      currentUploadStage.value = 'error'

      uploadResult.value = {
        status: 'error',
        message: errorMsg,
        failed: uploadProgress.value.errors,
        uploaded: uploadProgress.value.uploaded,
        replaced: uploadProgress.value.replaced
      }

      toast.add({ severity: 'error', summary: 'Ошибка', detail: errorMsg, life: 7000 })
    } finally {
      uploading.value = false
      canCancelUpload.value = false
    }
  }

  const cancelZipUpload = (closeDialogFn) => {
    if (!uploading.value) {
      if (closeDialogFn) closeDialogFn()
      selectedZipFile.value = null
      resetUploadState()
      zipForceOverwrite.value = false
    }
  }

  const acknowledgeUploadResult = (closeDialogFn) => {
    if (closeDialogFn) closeDialogFn()
    selectedZipFile.value = null
    resetUploadState()
    zipForceOverwrite.value = false
  }

  return {
    selectedImages, uploading, imageUploadProgress,
    onImageFilesChange, clearSelectedImages, removeSelectedImage, uploadImages,
    selectedZipFile, zipForceOverwrite, uploadProgress, uploadStages,
    currentUploadStage, uploadStatus, canCancelUpload, uploadFinished, uploadResult, uploadStats,
    onZipSelect, uploadZip, cancelZipUpload, acknowledgeUploadResult, resetUploadState,
    formatDuration, isShallowPath
  }
}
